import {
  Tooltip,
  flattenErrorTypeArray,
  ModelForm,
  ModelMultiSelect,
  Flex,
  useIsXOverflow,
  ValidIcon,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import { useRef } from 'react';
import {
  creditAddressSort,
  CreditItem,
  Insolvency,
  NameMatchedData,
  NameMatchStatusTypes,
  useCreditContext,
} from '../../contexts/credit';
import CreditTableItem from './CreditTableItem';
import Section from '../Account/components/Section';
import { SectionTitleIcon } from '../Account/Icons/SectionTitleIcon';

const ApplicantNameConatiner = styled.div`
  max-width: 100%;
`;

const ApplicantNameSelectContainer = styled(Flex)`
  width: 100%;
  height: 100%;
  align-items: center;
`;

const ApplicantNameText = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const multiSelectCss = css` ${({ theme }) => `
  width: 100%;
  .multi-select-parent {
    min-width: auto;
  }
  .dropdown-content {
    min-width: ${theme.dropdownWidth};
  }
  .mi-chevron-up, .mi-chevron-down {
    font-size: ${theme.fontSize[3]};
  }
`}`;

const findNamesMatchedData = (nameMatchedData: NameMatchedData[]) => nameMatchedData?.filter(
  ({ nameMatchStatus }) => nameMatchStatus === NameMatchStatusTypes.PRIMARY
    || nameMatchStatus === NameMatchStatusTypes.SECONDARY,
);

const errorContainerCss = css`${({ theme }) => `
  padding: 0 ${theme.padding[4]};
`}`;

export function ApplicantName(
  id: string,
  isEquifax: boolean,
  disabled: boolean,
  nameMatchedData: NameMatchedData[],
  applicants: NameMatchedData[],
  creditItem: CreditItem | Insolvency,
  update: (values: any, disableIncrementUpdateCount?: boolean) => Promise<void>,
) {
  const nameRef = useRef<HTMLDivElement>(null);
  const data = nameMatchedData;

  const {
    credit,
  } = useCreditContext();

  const { errors, warnings } = credit;

  const isTruncated = useIsXOverflow(nameRef);

  const validApplicants = applicants.filter((applicant) => ['A', 'B'].includes(applicant.nameMatchStatus));

  if (isEquifax || (validApplicants && validApplicants?.length === 1)) {
    const matchedApplicants = findNamesMatchedData(isEquifax ? data : validApplicants);

    let name = '';

    if (matchedApplicants.length === 1) {
      const { firstName, lastName } = matchedApplicants[0];

      name = `${firstName} ${lastName}`;
    }
    if (matchedApplicants.length > 1) {
      name = matchedApplicants.map((applicant) => `${applicant.firstName[0]}. ${applicant.lastName}`).join(', ');
    }

    return (
      <ApplicantNameConatiner id={`credit-item-name-${id}`}>
        <ApplicantNameText ref={nameRef}>
          {name}
        </ApplicantNameText>
        {name && isTruncated && (
          <Tooltip
            id={`credit-item-name-${id}`}
            html={name}
            place="top"
          />
        )}
      </ApplicantNameConatiner>
    );
  }

  return (
    <ApplicantNameSelectContainer>
      <ModelForm model={creditItem} update={update} errors={errors} warnings={warnings}>
        <ModelMultiSelect
          id={id}
          field="applicantIds"
          options={validApplicants.reduce((prev, data) => ({
            ...prev,
            [data?.id]: `${data?.firstName} ${data?.lastName}`,
          }), {})}
          isTooltipError
          placeholder="Please select"
          containerCss={multiSelectCss}
          disabled={disabled}
        />
      </ModelForm>
    </ApplicantNameSelectContainer>
  );
}

export default function CreditTables({
  type,
}: {
  type: 'secured' | 'unsecured',
}) {
  const {
    credit,
    securedCreditAddresses,
    unsecuredCreditAddresses,
    expandedStates,
    handleToggle,
  } = useCreditContext();

  const { errors, warnings } = credit;

  const addresses = {
    securedCreditAddresses,
    unsecuredCreditAddresses,
  }[`${type}CreditAddresses`];

  const creditItemErrors = Object.keys(credit[`${type}CreditItems`]?.creditItems ?? {}).map((id) => errors?.[id] ?? []).flat();
  const combinedErrors = flattenErrorTypeArray([
    ...(errors?.[credit[`${type}CreditItems`].id] ?? []),
    ...creditItemErrors,
  ]);

  const refName = `${type}-credit-table`;

  return (
    <Section
      id={credit[`${type}CreditItems`].id}
      refName={refName}
      title={`${type.charAt(0).toUpperCase() + type.slice(1)} Credit`}
      titleIcon={(
        <SectionTitleIcon
          errors={combinedErrors}
          tickIcon={<ValidIcon inverted />}
        />
      )}
      errorContainerCss={errorContainerCss}
      errors={errors}
      warnings={warnings}
      collapsed={!expandedStates[refName]}
      onClickCollapse={() => handleToggle(refName)}
    >
      {
        Object.values(addresses).sort(creditAddressSort).map((address) => <CreditTableItem type={type} address={address} key={address?.id} />)
      }
    </Section>
  );
}
